import React from 'react';
import {
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Chip,
  Stack,
  LinearProgress,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssistantIcon from '@mui/icons-material/Assistant';
import ArchiveIcon from '@mui/icons-material/Archive';
import EmailIcon from '@mui/icons-material/Email';

const AccountManagement = ({
  subscriptionStatus,
  customerDetails,
  quotaUsage,
  loadingQuota,
  agentEnabled,
  archiveSoldItems,
  togglingAgent,
  togglingArchive,
  mailingListLoading,
  mailingListSubscribed,
  mailingListSubmitting,
  mailingListError,
  onToggleAgent,
  onToggleArchiveSoldItems,
  onToggleMailingList,
  billingPortalLoading,
  billingPortalError,
  onOpenBillingPortal,
}) => {
  const ownerEmail = customerDetails?.email || 'N/A';
  const hasAccountDetails = Boolean(customerDetails?.accountId || customerDetails?.accountName);

  const getSubscriptionStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatTrialEndDate = (trialEnd) => {
    if (!trialEnd) return null;
    const ms = typeof trialEnd === 'object' && trialEnd._seconds
      ? trialEnd._seconds * 1000
      : new Date(trialEnd).getTime();
    const date = new Date(ms);
    return isNaN(date.getTime()) ? null : date.toLocaleDateString();
  };

  const hasActiveSubscription = subscriptionStatus?.hasSubscription &&
    (subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'trialing');

  const subscriptionCardBorderColor = hasActiveSubscription ? 'hsl(133, 60%, 22%)' : '#d32f2f';
  const subscriptionIconColor = hasActiveSubscription ? 'hsl(133, 60%, 22%)' : '#d32f2f';

  const getQuotaPercentage = (used, limit) => {
    if (!limit) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getQuotaColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'primary';
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Account Info Section */}
      <Card
        sx={{
          mb: 3,
          backgroundColor: 'grey.50',
          borderLeftWidth: 4, borderLeftStyle: 'solid', borderLeftColor: 'primary.dark',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AssignmentIcon sx={{ fontSize: 28, color: 'primary.dark' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Account Information
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />

          {hasAccountDetails ? (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                >
                  Owner Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {ownerEmail}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                >
                  Owner Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {customerDetails?.customerName || 'N/A'}
                </Typography>
              </Grid>

            </Grid>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              No account information available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Subscription Section */}
      <Card
        sx={{
          backgroundColor: 'grey.50',
          borderLeft: `4px solid ${subscriptionCardBorderColor}`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <CreditCardIcon sx={{ fontSize: 28, color: subscriptionIconColor }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Subscription
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />

          {subscriptionStatus && subscriptionStatus.hasSubscription ? (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                >
                  Plan
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {subscriptionStatus.plan || 'Unknown Plan'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                >
                  Status
                </Typography>
                <Chip
                  label={subscriptionStatus.status === 'trialing' ? 'Trial' : subscriptionStatus.status}
                  color={getSubscriptionStatusColor(subscriptionStatus.status)}
                  variant="filled"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Grid>

              {subscriptionStatus.status === 'trialing' && subscriptionStatus.trialEnd && (
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                  >
                    Trial Ends
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {formatTrialEndDate(subscriptionStatus.trialEnd)}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                >
                  Subscription ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    wordBreak: 'break-all',
                    fontSize: '0.85rem',
                  }}
                >
                  {subscriptionStatus.subscriptionId || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 500 }}
                  >
                    Manage Subscription
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    To update your payment method, change your plan, or cancel your subscription, visit the Stripe customer portal.
                  </Typography>
                  {billingPortalError && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {billingPortalError}
                    </Alert>
                  )}
                  {subscriptionStatus.subscriptionId && (
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={onOpenBillingPortal}
                        disabled={billingPortalLoading}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {billingPortalLoading ? (
                          <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                        ) : null}
                        {billingPortalLoading ? 'Opening...' : 'Manage Subscription \u2192'}
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ color: 'text.disabled', mb: 2 }}>
                No active subscription
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                To start your subscription, please contact support or sign up for a plan.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Subscription Usage Section */}
      {hasActiveSubscription && (
        <Card
          sx={{
            mt: 3,
            backgroundColor: 'grey.50',
            borderLeftWidth: 4, borderLeftStyle: 'solid', borderLeftColor: 'primary.dark',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <BarChartIcon sx={{ fontSize: 28, color: 'primary.dark' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Subscription Usage
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />

            {loadingQuota ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Loading usage data...
              </Typography>
            ) : quotaUsage ? (
              <Grid container spacing={3}>
                {/* Inventory Items */}
                {quotaUsage.items && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                    >
                      Inventory Items
                      {quotaUsage.items.scope && (
                        <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5 }}>
                          ({quotaUsage.items.scope})
                        </Typography>
                      )}
                    </Typography>
                    {quotaUsage.items.channels ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {quotaUsage.items.channels.map((ch) => {
                          const pct = getQuotaPercentage(ch.used, quotaUsage.items.limit);
                          return (
                            <Box key={ch.channelId}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                  {ch.channelName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {ch.used.toLocaleString()} / {quotaUsage.items.limit.toLocaleString()} ({Math.round(pct)}%)
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                sx={{ height: 5, borderRadius: 3 }}
                                color={getQuotaColor(pct)}
                              />
                            </Box>
                          );
                        })}
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                          Total: {quotaUsage.items.totalUsed.toLocaleString()} items across {quotaUsage.items.channels.length} {quotaUsage.items.channels.length === 1 ? 'channel' : 'channels'}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {quotaUsage.items.used} / {quotaUsage.items.limit}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {Math.round(getQuotaPercentage(quotaUsage.items.used, quotaUsage.items.limit))}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={getQuotaPercentage(quotaUsage.items.used, quotaUsage.items.limit)}
                          sx={{ height: 6, borderRadius: 3 }}
                          color={getQuotaColor(getQuotaPercentage(quotaUsage.items.used, quotaUsage.items.limit))}
                        />
                      </>
                    )}
                  </Grid>
                )}

                {/* Images */}
                {quotaUsage.images && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                    >
                      Images per Item
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Up to {quotaUsage.images.limit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={100}
                      sx={{ height: 6, borderRadius: 3 }}
                      color="success"
                    />
                  </Grid>
                )}

                {/* Sales Channels */}
                {quotaUsage.salesChannels && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                    >
                      Sales Channels
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {quotaUsage.salesChannels.used} / {quotaUsage.salesChannels.limit}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(getQuotaPercentage(quotaUsage.salesChannels.used, quotaUsage.salesChannels.limit))}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getQuotaPercentage(quotaUsage.salesChannels.used, quotaUsage.salesChannels.limit)}
                      sx={{ height: 6, borderRadius: 3 }}
                      color={getQuotaColor(getQuotaPercentage(quotaUsage.salesChannels.used, quotaUsage.salesChannels.limit))}
                    />
                  </Grid>
                )}

                {/* Expenses */}
                {quotaUsage.expenses && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                    >
                      Expenses Tracked
                      {quotaUsage.expenses.scope && (
                        <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5 }}>
                          ({quotaUsage.expenses.scope})
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {quotaUsage.expenses.used.toLocaleString()} / {quotaUsage.expenses.limit.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(getQuotaPercentage(quotaUsage.expenses.used, quotaUsage.expenses.limit))}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getQuotaPercentage(quotaUsage.expenses.used, quotaUsage.expenses.limit)}
                      sx={{ height: 6, borderRadius: 3 }}
                      color={getQuotaColor(getQuotaPercentage(quotaUsage.expenses.used, quotaUsage.expenses.limit))}
                    />
                  </Grid>
                )}

                {/* Sales Transactions */}
                {quotaUsage.salesTransactions && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                    >
                      Sales Transactions
                      {quotaUsage.salesTransactions.scope && (
                        <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5 }}>
                          ({quotaUsage.salesTransactions.scope})
                        </Typography>
                      )}
                    </Typography>
                    {quotaUsage.salesTransactions.channels ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {quotaUsage.salesTransactions.channels.map((ch) => {
                          const pct = getQuotaPercentage(ch.used, quotaUsage.salesTransactions.limit);
                          return (
                            <Box key={ch.channelId}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                  {ch.channelName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {ch.used.toLocaleString()} / {quotaUsage.salesTransactions.limit.toLocaleString()} ({Math.round(pct)}%)
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                sx={{ height: 5, borderRadius: 3 }}
                                color={getQuotaColor(pct)}
                              />
                            </Box>
                          );
                        })}
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                          Total this month: {quotaUsage.salesTransactions.totalUsed.toLocaleString()} transactions across {quotaUsage.salesTransactions.channels.length} {quotaUsage.salesTransactions.channels.length === 1 ? 'channel' : 'channels'}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {quotaUsage.salesTransactions.used} / {quotaUsage.salesTransactions.limit}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {Math.round(getQuotaPercentage(quotaUsage.salesTransactions.used, quotaUsage.salesTransactions.limit))}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={getQuotaPercentage(quotaUsage.salesTransactions.used, quotaUsage.salesTransactions.limit)}
                          sx={{ height: 6, borderRadius: 3 }}
                          color={getQuotaColor(getQuotaPercentage(quotaUsage.salesTransactions.used, quotaUsage.salesTransactions.limit))}
                        />
                      </>
                    )}
                  </Grid>
                )}

                {/* Connected Sales Channels */}
                {quotaUsage.connectedSalesChannels && quotaUsage.connectedSalesChannels.limit > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                    >
                      Connected Sales Channels
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {quotaUsage.connectedSalesChannels.used} / {quotaUsage.connectedSalesChannels.limit}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(getQuotaPercentage(quotaUsage.connectedSalesChannels.used, quotaUsage.connectedSalesChannels.limit))}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getQuotaPercentage(quotaUsage.connectedSalesChannels.used, quotaUsage.connectedSalesChannels.limit)}
                      sx={{ height: 6, borderRadius: 3 }}
                      color={getQuotaColor(getQuotaPercentage(quotaUsage.connectedSalesChannels.used, quotaUsage.connectedSalesChannels.limit))}
                    />
                  </Grid>
                )}

                {/* AI Agent Tokens */}
                {quotaUsage.agentTokens && quotaUsage.agentTokens.used != null && quotaUsage.agentTokens.limit != null && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 500 }}
                    >
                      AI Agent Tokens (Monthly)
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {quotaUsage.agentTokens.used.toLocaleString()} / {quotaUsage.agentTokens.limit.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(getQuotaPercentage(quotaUsage.agentTokens.used, quotaUsage.agentTokens.limit))}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getQuotaPercentage(quotaUsage.agentTokens.used, quotaUsage.agentTokens.limit)}
                      sx={{ height: 6, borderRadius: 3 }}
                      color={getQuotaColor(getQuotaPercentage(quotaUsage.agentTokens.used, quotaUsage.agentTokens.limit))}
                    />
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Usage data not available
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Assistant Settings */}
      {hasActiveSubscription && (
        <Card
          sx={{
            mt: 3,
            backgroundColor: 'grey.50',
            borderLeftWidth: 4, borderLeftStyle: 'solid', borderLeftColor: 'primary.dark',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <AssistantIcon sx={{ fontSize: 28, color: 'primary.dark' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                AI Assistant
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Enable AI Assistant
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Show the AI assistant chat icon across the app
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={agentEnabled}
                    onChange={onToggleAgent}
                    disabled={togglingAgent}
                    color="primary"
                  />
                }
                label=""
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Auto-Archive Settings */}
      {hasActiveSubscription && (
        <Card
          sx={{
            mt: 3,
            backgroundColor: 'grey.50',
            borderLeftWidth: 4, borderLeftStyle: 'solid', borderLeftColor: 'primary.dark',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <ArchiveIcon sx={{ fontSize: 28, color: 'primary.dark' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Inventory Archive
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Auto-archive sold items
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Automatically archive items when marked as sold with 0 quantity remaining
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={archiveSoldItems}
                    onChange={onToggleArchiveSoldItems}
                    disabled={togglingArchive}
                    color="primary"
                  />
                }
                label=""
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Email Preferences */}
      {hasActiveSubscription && (
        <Card
          sx={{
            mt: 3,
            backgroundColor: 'grey.50',
            borderLeftWidth: 4, borderLeftStyle: 'solid', borderLeftColor: 'primary.dark',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <EmailIcon sx={{ fontSize: 28, color: 'primary.dark' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Email Preferences
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Newsletter &amp; Updates
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {mailingListSubscribed
                    ? 'You are subscribed to marketing emails and product update newsletters.'
                    : 'Subscribe to receive marketing emails and product update newsletters.'}
                </Typography>
                {mailingListSubscribed === false && (
                  <Typography variant="body2" sx={{ color: '#888', mt: 0.5, fontStyle: 'italic' }}>
                    You will still receive account-related emails such as payment reminders and subscription changes.
                  </Typography>
                )}
              </Box>
              {mailingListLoading ? (
                <CircularProgress size={20} />
              ) : mailingListSubscribed != null && (
                <Button
                  variant="outlined"
                  size="small"
                  color={mailingListSubscribed ? 'inherit' : 'primary'}
                  onClick={onToggleMailingList}
                  disabled={mailingListSubmitting}
                  sx={{ textTransform: 'none' }}
                >
                  {mailingListSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : mailingListSubscribed ? (
                    'Unsubscribe'
                  ) : (
                    'Subscribe'
                  )}
                </Button>
              )}
            </Box>
            {mailingListError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {mailingListError}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AccountManagement;
